/** Why a press ended without the input device telling us. */
export type PressInterruptReason = "blur" | "hidden";

/**
 * The single definition of "this press can no longer be completed".
 *
 * Three surfaces need it — the space-bar draw lock, the hold-key machine, and
 * the scratchpad's pointer stroke — and each used to assemble its own subset.
 * Two of them omitted `pagehide`, so an iOS Safari backgrounding or a bfcache
 * navigation left a microphone recording with no listener to stop it, and a
 * stroke neither committed nor discarded. One list, so a site cannot silently
 * disagree about what "interrupted" means.
 *
 * `pagehide` rather than `beforeunload`: it fires for bfcache navigations too,
 * and does not block the page from being cached.
 *
 * Returns an unsubscribe.
 */
export function onPressInterrupt(
  handler: (reason: PressInterruptReason) => void,
): () => void {
  const onBlur = () => handler("blur");
  const onPageHide = () => handler("hidden");
  const onVisibilityChange = () => {
    // Only the hidden direction ends a press; coming back must not.
    if (document.visibilityState !== "visible") handler("hidden");
  };

  globalThis.addEventListener("blur", onBlur);
  globalThis.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    globalThis.removeEventListener("blur", onBlur);
    globalThis.removeEventListener("pagehide", onPageHide);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}
